<template>
  <component
    :is="computedElement"
    :id="id"
    :class="computedClasses"
    :to="to"
    :target="target"
    :href="computedHref"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
import { useCssModule } from 'vue'
import { NuxtLinkLocale } from '#components'

const $style = useCssModule()

const props = defineProps<{
  id?: string
  to?: string
  target?: '_blank' | '_self' | '_parent' | '_top'
  size?: 'default' | 'large' | 'small'
  href?: string
  nativeLink?: boolean
  presentation?:
    | 'default'
    | 'dark'
    | 'light'
    | 'invisible'
    | 'strip'
    | 'two-tones'
    | 'clear'
    | 'outline'
    | 'translucent'
    | 'orange'
}>()

const computedElement = computed(() => {
  if (props.href) return 'a'
  if (props.nativeLink && props.to) return 'a'
  if (props.to) return NuxtLinkLocale
  return 'button'
})

const computedHref = computed(() => {
  if (props.href) return props.href
  if (props.nativeLink) return props.to
  return undefined
})

const computedClasses = computed(() => ({
  [$style.config]: true,
  [$style.button]: true,
  [$style.large]: props.size === 'large',
  [$style.small]: props.size === 'small',
  [$style.invisible]: props.presentation === 'invisible',
  [$style.strip]: props.presentation === 'strip',
  [$style.twoTones]: props.presentation === 'two-tones',
  [$style.clear]: props.presentation === 'clear',
  [$style.outline]: props.presentation === 'outline',
  [$style.dark]: props.presentation === 'dark',
  [$style.light]: props.presentation === 'light',
  [$style.translucent]: props.presentation === 'translucent',
  [$style.orange]: props.presentation === 'orange',
}))
</script>

<style lang="scss" module>
.config {
  --button-background: var(--purple);
  --button-background--hover: var(--purple-light);
  --button-background--active: var(--purple-dark);
  --button-border: 1px solid transparent;
  --button-border-radius: 5px;

  --button-color: var(--white);
  --button-font-family: var(--roboto-condensed);
  --button-font-size: 18px;
  --button-font-weight: bold;

  --button-min-width: 150px;
  --button-padding: 0.5em 1.45em;

  &.large {
    --button-font-size: 22px;

    @include media('md') {
      --button-font-size: 26px;
    }
  }

  &.small {
    --button-font-size: 14px;
    --button-padding: 0.4em 1em;
    --button-min-width: 120px;
  }

  &.invisible {
    position: absolute;
    inset: 0;
    background: transparent;
    border: none;
    opacity: 0;
  }

  &.strip {
    background: transparent;
    --button-border: none;

    color: inherit;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    text-transform: none;
    text-align: left;

    --button-padding: 0;
    min-width: unset;

    &:active,
    &:focus,
    &:hover {
      background: transparent;
      color: inherit;
    }
  }

  &.twoTones {
    position: relative;
    z-index: 1;
  }

  &.twoTones::after {
    content: '';
    background: var(--black-alpha10);
    position: absolute;
    top: 0;
    right: 0;
    width: 30px;
    height: 100%;
    z-index: -1;
  }

  &.large.twoTones::after {
    width: 35px;
  }

  &.clear {
    --button-background: transparent;
    --button-background--hover: transparent;
    --button-background--active: transparent;
    --button-color: var(--purple);

    &:active,
    &:focus,
    &:hover {
      --button-color: var(--purple-light);
    }
  }

  &.outline {
    --button-color: var(--purple);
    --button-background: transparent;
    --button-background--hover: transparent;
    --button-background--active: transparent;
    --button-border: 1px solid var(--purple);

    &:active,
    &:focus,
    &:hover {
      --button-color: var(--purple-light);
      --button-border: 1px solid var(--purple-light);
    }
  }

  &.dark {
    --button-background: var(--gray-8);
    --button-background--hover: var(--gray-7);
  }

  &.light {
    --button-color: var(--purple);
    --button-background: var(--white);
    --button-background--hover: var(--gray-4);
  }

  &.translucent {
    --button-background: rgba(0, 0, 0, 0.3);
    --button-background--hover: rgba(0, 0, 0, 0.5);
  }
  &.orange {
    --button-background: var(--orange);
    --button-background--hover: var(--orange-light);

    &:hover,
    &:focus {
      color: var(--white);
    }
  }
}

.config.button {
  appearance: none; // Removes the browser's native button styling to make <button> and <a> elements look the same
  -webkit-appearance: none; // Removes the browser's native button styling to make <button> and <a> elements look the same
  box-sizing: border-box;
  line-height: 1;
  margin: 0;

  background: var(--button-background);
  border-radius: var(--button-border-radius);
  border: var(--button-border);
  transition: cubic-bezier(0.16, 1, 0.29, 0.99) 0.2s;

  color: var(--button-color);
  font-family: var(--button-font-family);
  font-size: var(--button-font-size);
  font-weight: var(--button-font-weight);
  text-decoration: none;

  display: inline-flex;
  justify-content: center;

  padding: var(--button-padding);
  min-width: var(--button-min-width);
  outline: none; // remove browser default focus state

  &:hover,
  &:focus {
    background: var(--button-background--hover);
  }

  &:active {
    background: var(--button-background--active);
  }

  // remove browser default focus state
  &::-moz-focus-inner {
    border: none;
  }
}

.config.strip.button {
  color: inherit;
  text-decoration: none;
}

</style>
